/* eslint-disable no-use-before-define */
import React, { useContext, useState, useEffect } from 'react';
import FormItem from 'antd/es/form/FormItem';
import {
  Form,
  Input,
  Button,
  DatePicker,
  Select,
  Avatar,
  message,
  Col,
  Row,
  Typography,
  theme,
} from 'antd';
import { motion } from 'framer-motion';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import {
  signInWithPopup,
  GoogleAuthProvider,
  RecaptchaVerifier,
  linkWithPhoneNumber,
  signOut,
} from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  CalendarOutlined,
  TrophyOutlined,
  BarChartOutlined,
  BgColorsOutlined,
  MessageOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { addUser, getUserByUid } from './api';
import { addMessagingTokenToUser } from './utils';
import { reverseGeocodeCity } from '../utils';
import { StoreContext } from '../store/index';
import { auth } from '../firebase';
import { ROLES, LANGUAGES, POSITION, LEVELS } from '../constants';
import AppLogo from '../components/AppLogo';
import PitchBackground from '../components/PitchBackground';
import ShiftCreatedOverlay from '../components/ShiftCreatedOverlay';
import SearchLocationInput from '../components/mapsAddress';

// Priority defaults (España, Argentina, México, USA…) surface first; the rest
// is a broad worldwide list so people from other markets can sign up too.
const COUNTRY_CODES = [
  { code: '34', flag: '🇪🇸', label: 'España' },
  { code: '54', flag: '🇦🇷', label: 'Argentina' },
  { code: '52', flag: '🇲🇽', label: 'México' },
  { code: '1', flag: '🇺🇸', label: 'USA / Canadá' },
  { code: '44', flag: '🇬🇧', label: 'Reino Unido' },
  { code: '33', flag: '🇫🇷', label: 'Francia' },
  { code: '49', flag: '🇩🇪', label: 'Alemania' },
  { code: '39', flag: '🇮🇹', label: 'Italia' },
  { code: '351', flag: '🇵🇹', label: 'Portugal' },
  { code: '55', flag: '🇧🇷', label: 'Brasil' },
  { code: '212', flag: '🇲🇦', label: 'Marruecos' },
  { code: '966', flag: '🇸🇦', label: 'Arabia Saudita' },
  { code: '61', flag: '🇦🇺', label: 'Australia' },
  { code: '64', flag: '🇳🇿', label: 'Nueva Zelanda' },
  { code: '353', flag: '🇮🇪', label: 'Irlanda' },
  { code: '31', flag: '🇳🇱', label: 'Países Bajos' },
  { code: '32', flag: '🇧🇪', label: 'Bélgica' },
  { code: '41', flag: '🇨🇭', label: 'Suiza' },
  { code: '43', flag: '🇦🇹', label: 'Austria' },
  { code: '45', flag: '🇩🇰', label: 'Dinamarca' },
  { code: '46', flag: '🇸🇪', label: 'Suecia' },
  { code: '47', flag: '🇳🇴', label: 'Noruega' },
  { code: '358', flag: '🇫🇮', label: 'Finlandia' },
  { code: '354', flag: '🇮🇸', label: 'Islandia' },
  { code: '48', flag: '🇵🇱', label: 'Polonia' },
  { code: '420', flag: '🇨🇿', label: 'Chequia' },
  { code: '421', flag: '🇸🇰', label: 'Eslovaquia' },
  { code: '36', flag: '🇭🇺', label: 'Hungría' },
  { code: '40', flag: '🇷🇴', label: 'Rumania' },
  { code: '30', flag: '🇬🇷', label: 'Grecia' },
  { code: '90', flag: '🇹🇷', label: 'Turquía' },
  { code: '380', flag: '🇺🇦', label: 'Ucrania' },
  { code: '7', flag: '🇷🇺', label: 'Rusia' },
  { code: '972', flag: '🇮🇱', label: 'Israel' },
  { code: '971', flag: '🇦🇪', label: 'Emiratos Árabes Unidos' },
  { code: '974', flag: '🇶🇦', label: 'Catar' },
  { code: '965', flag: '🇰🇼', label: 'Kuwait' },
  { code: '973', flag: '🇧🇭', label: 'Baréin' },
  { code: '968', flag: '🇴🇲', label: 'Omán' },
  { code: '962', flag: '🇯🇴', label: 'Jordania' },
  { code: '961', flag: '🇱🇧', label: 'Líbano' },
  { code: '20', flag: '🇪🇬', label: 'Egipto' },
  { code: '213', flag: '🇩🇿', label: 'Argelia' },
  { code: '216', flag: '🇹🇳', label: 'Túnez' },
  { code: '27', flag: '🇿🇦', label: 'Sudáfrica' },
  { code: '234', flag: '🇳🇬', label: 'Nigeria' },
  { code: '254', flag: '🇰🇪', label: 'Kenia' },
  { code: '91', flag: '🇮🇳', label: 'India' },
  { code: '92', flag: '🇵🇰', label: 'Pakistán' },
  { code: '880', flag: '🇧🇩', label: 'Bangladés' },
  { code: '62', flag: '🇮🇩', label: 'Indonesia' },
  { code: '60', flag: '🇲🇾', label: 'Malasia' },
  { code: '65', flag: '🇸🇬', label: 'Singapur' },
  { code: '66', flag: '🇹🇭', label: 'Tailandia' },
  { code: '84', flag: '🇻🇳', label: 'Vietnam' },
  { code: '63', flag: '🇵🇭', label: 'Filipinas' },
  { code: '81', flag: '🇯🇵', label: 'Japón' },
  { code: '82', flag: '🇰🇷', label: 'Corea del Sur' },
  { code: '86', flag: '🇨🇳', label: 'China' },
  { code: '852', flag: '🇭🇰', label: 'Hong Kong' },
  { code: '886', flag: '🇹🇼', label: 'Taiwán' },
  { code: '56', flag: '🇨🇱', label: 'Chile' },
  { code: '57', flag: '🇨🇴', label: 'Colombia' },
  { code: '58', flag: '🇻🇪', label: 'Venezuela' },
  { code: '51', flag: '🇵🇪', label: 'Perú' },
  { code: '593', flag: '🇪🇨', label: 'Ecuador' },
  { code: '591', flag: '🇧🇴', label: 'Bolivia' },
  { code: '595', flag: '🇵🇾', label: 'Paraguay' },
  { code: '598', flag: '🇺🇾', label: 'Uruguay' },
  { code: '506', flag: '🇨🇷', label: 'Costa Rica' },
  { code: '507', flag: '🇵🇦', label: 'Panamá' },
  { code: '502', flag: '🇬🇹', label: 'Guatemala' },
  { code: '503', flag: '🇸🇻', label: 'El Salvador' },
  { code: '504', flag: '🇭🇳', label: 'Honduras' },
  { code: '505', flag: '🇳🇮', label: 'Nicaragua' },
  { code: '53', flag: '🇨🇺', label: 'Cuba' },
  { code: '1809', flag: '🇩🇴', label: 'República Dominicana' },
  { code: '1787', flag: '🇵🇷', label: 'Puerto Rico' },
];

// Google OAuth is blocked inside in-app webviews (Instagram, Facebook,
// TikTok...): the popup loads forever and bounces back. Detect them and
// guide the user to a real browser instead of showing a broken button.
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

const isInAppBrowser = () => {
  const ua = navigator.userAgent;
  if (/instagram|fban|fbav|fb_iab|tiktok|musical_ly|line\//i.test(ua)) return true;
  // Some iOS in-app browsers don't identify themselves in the UA, but iOS
  // webviews never carry the Safari token (every real iOS browser does).
  // The installed PWA also lacks it — navigator.standalone excludes it.
  return isIos() && !/safari/i.test(ua) && !window.navigator.standalone;
};

// Landing info for anonymous visitors — reuses the news page i18n keys
const HOW_STEPS = [
  { emoji: '📅', titleId: 'news.how.step1.title', descId: 'news.how.step1.desc' },
  { emoji: '👥', titleId: 'news.how.step2.title', descId: 'news.how.step2.desc' },
  { emoji: '⚽', titleId: 'news.how.step3.title', descId: 'news.how.step3.desc' },
];

const FEATURES = [
  { titleId: 'news.feature.games.title', descId: 'news.feature.games.desc', icon: <CalendarOutlined />, accent: '#1677ff' },
  { titleId: 'news.feature.leagues.title', descId: 'news.feature.leagues.desc', icon: <TrophyOutlined />, accent: '#f59e0b' },
  { titleId: 'news.feature.stats.title', descId: 'news.feature.stats.desc', icon: <BarChartOutlined />, accent: '#22c55e' },
  { titleId: 'news.feature.teams.title', descId: 'news.feature.teams.desc', icon: <BgColorsOutlined />, accent: '#a855f7' },
  { titleId: 'news.feature.chat.title', descId: 'news.feature.chat.desc', icon: <MessageOutlined />, accent: '#06b6d4' },
  { titleId: 'news.feature.friends.title', descId: 'news.feature.friends.desc', icon: <UserAddOutlined />, accent: '#f43f5e' },
];

const Login = () => {
  const store = useContext(StoreContext);
  const {
    user: [, setUser, setUpdateUser],
  } = store;
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [onboardingForm] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { formatMessage } = useIntl();

  const [loggedMail, setLoggedMail] = useState();
  const [loading, setLoading] = useState(false);
  const [loadingGPS, setLoadingGPS] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [onboardingUser, setOnboardingUser] = useState(null);
  const [showAccountCreated, setShowAccountCreated] = useState(false);
  const [locationData, setLocationData] = useState(null);

  const getDestination = () =>
    location.search !== '' ? `/shifts/${location.search}` : '/';

  const inApp = isInAppBrowser();

  const handleOpenExternal = () => {
    const { host, pathname, search } = window.location;
    if (/android/i.test(navigator.userAgent)) {
      // Android: intent URL escapes the webview into the default browser
      window.location.href = `intent://${host}${pathname}${search}#Intent;scheme=https;end`;
    } else {
      // iOS: cannot escape programmatically — copy the link and instruct
      navigator.clipboard
        ?.writeText(window.location.href)
        .then(() => message.success(formatMessage({ id: 'login.inapp.copied' })))
        .catch(() => message.info(window.location.href));
    }
  };

  const showPopup = () => {
    signInWithPopup(auth, new GoogleAuthProvider())
      .then(async result => {
        // Source of truth is the `users` doc, not the linked phone. A user can
        // have a phone linked in Auth but no doc yet (bailed mid-onboarding).
        const existingUser = await getUserByUid(result.user.uid);
        if (existingUser) {
          setUser({ ...result.user, friendsList: [], friendsIds: [], friends: [], requests: [] });
          setUpdateUser(true);
          addMessagingTokenToUser(result.user.uid);
          navigate(getDestination());
        } else if (result.user.phoneNumber) {
          // Auth session + phone already verified, just missing the profile
          goToOnboarding(buildOnboardingUser(result.user));
        } else {
          setLoggedMail(result.user);
        }
      })
      .catch(() => {});
  };

  const resetRecaptcha = () => {
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear();
      window.recaptchaVerifier = null;
    }
  };

  const onSignup = () => {
    setLoading(true);
    resetRecaptcha();
    window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
      size: 'invisible',
    });
    const { phone, countryCode } = form.getFieldsValue();
    const fullNumber = `+${countryCode}${phone}`;
    linkWithPhoneNumber(auth.currentUser, fullNumber, window.recaptchaVerifier)
      .then(confirmationResult => {
        window.confirmationResult = confirmationResult;
        setLoading(false);
        setShowOTP(true);
        message.success(formatMessage({ id: 'login.otp.sent' }));
      })
      .catch(err => {
        resetRecaptcha();
        setLoading(false);
        // Log the raw Firebase error so we can diagnose future reports even
        // when the user only screenshots the toast.
        // eslint-disable-next-line no-console
        console.error('[login] linkWithPhoneNumber failed', err);
        const code = err?.code || '';
        let msgId = 'login.phone.invalid';
        if (code === 'auth/credential-already-in-use') msgId = 'login.phone.error.alreadyInUse';
        else if (code === 'auth/provider-already-linked') msgId = 'login.phone.error.alreadyLinked';
        else if (code === 'auth/too-many-requests') msgId = 'login.phone.error.tooManyRequests';
        else if (code === 'auth/quota-exceeded') msgId = 'login.phone.error.quotaExceeded';
        else if (code === 'auth/captcha-check-failed') msgId = 'login.phone.error.captcha';
        else if (code === 'auth/invalid-phone-number') msgId = 'login.phone.invalid';
        if (msgId === 'login.phone.invalid' && code && code !== 'auth/invalid-phone-number') {
          // Unknown error: surface the raw code so the user can share it.
          message.error(`${formatMessage({ id: 'login.phone.error.unknown' })} (${code})`);
        } else {
          message.error(formatMessage({ id: msgId }));
        }
      });
  };

  const getDefaultLanguage = () => {
    if (navigator.language.includes('es')) return LANGUAGES.SPANISH;
    if (navigator.language.includes('ar')) return LANGUAGES.ARABIC;
    return LANGUAGES.ENGLISH;
  };

  // Normalize an Auth user for onboarding, preferring the Google avatar
  const buildOnboardingUser = authUser => {
    const googleProvider = authUser.providerData.find(p => p.providerId === 'google.com');
    return { ...authUser, photoURL: googleProvider?.photoURL || authUser.photoURL || '' };
  };

  // Show the onboarding form pre-filled with the user's Google data
  const goToOnboarding = u => {
    setOnboardingUser(u);
    onboardingForm.setFieldsValue({
      nickname: u.displayName || '',
      photoURL: u.photoURL || '',
      languageSelected: getDefaultLanguage(),
      position: POSITION.DEFENCE,
      level: LEVELS.BASIC,
    });
  };

  const onOTPVerify = async () => {
    setLoading(true);
    window.confirmationResult
      .confirm(form.getFieldsValue().otp)
      .then(async res => {
        setLoading(false);
        const u = buildOnboardingUser(res.user);
        const existingUser = await getUserByUid(u.uid);
        if (existingUser) {
          setUser({ ...u, friendsList: [], friendsIds: [], friends: [], requests: [] });
          setUpdateUser(true);
          addMessagingTokenToUser(u.uid);
          navigate(getDestination());
        } else {
          // New user — show onboarding form pre-filled with Google data
          goToOnboarding(u);
        }
      })
      .catch(err => {
        setLoading(false);
        // eslint-disable-next-line no-console
        console.error('[login] confirm OTP failed', err);
        const code = err?.code || '';
        if (code === 'auth/code-expired') {
          message.error(formatMessage({ id: 'login.otp.error.expired' }));
        } else if (code === 'auth/invalid-verification-code') {
          message.error(formatMessage({ id: 'login.otp.invalid' }));
        } else if (code && code !== 'auth/invalid-verification-code') {
          message.error(`${formatMessage({ id: 'login.otp.error.unknown' })} (${code})`);
        } else {
          message.error(formatMessage({ id: 'login.otp.invalid' }));
        }
      });
  };

  // Refresh recovery: a persisted Auth session with no `users` doc means the
  // user bailed mid-signup. Resume the flow instead of dropping them on the
  // main login screen (or, worse, letting them into the app with no doc).
  useEffect(() => {
    let cancelled = false;
    auth.authStateReady().then(async () => {
      const current = auth.currentUser;
      if (cancelled || !current) return;
      const existingUser = await getUserByUid(current.uid);
      if (cancelled || existingUser) return; // registered → the store hydrates it
      const u = buildOnboardingUser(current);
      if (current.phoneNumber) goToOnboarding(u); // phone verified → onboarding
      else setLoggedMail(current); // needs phone verification first
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleGetGPSLocation = () => {
    setLoadingGPS(true);
    navigator.geolocation.getCurrentPosition(
      async pos => {
        const { latitude: lat, longitude: lng } = pos.coords;
        const city = await reverseGeocodeCity(lat, lng);
        setLocationData({ lat, lng, city });
        setLoadingGPS(false);
      },
      () => {
        message.error(formatMessage({ id: 'profile.location.gps.error' }));
        setLoadingGPS(false);
      },
    );
  };

  const handleCitySelect = ({ lat, lng, name }) => {
    if (lat && lng) {
      setLocationData({ lat, lng, city: name || null });
    }
  };

  const onOnboardingSubmit = async () => {
    let values;
    try {
      values = await onboardingForm.validateFields();
    } catch {
      return;
    }
    if (!locationData) {
      message.error(formatMessage({ id: 'profile.location.required' }));
      return;
    }
    setLoading(true);
    const { birthDate, ...rest } = values;
    const userData = {
      ...rest,
      email: onboardingUser.email,
      uid: onboardingUser.uid,
      phoneNumber: onboardingUser.phoneNumber || '',
      friendsIds: [],
      role: ROLES.USER,
      tokens: 0,
      location: locationData,
    };
    if (birthDate) {
      userData.birthDate = birthDate.format('YYYY-MM-DD');
      userData.age = dayjs().diff(birthDate, 'year');
    } else {
      userData.age = 0;
    }
    await addUser(userData, setLoading);
    setUser({ ...onboardingUser, friendsList: [], friendsIds: [], friends: [], requests: [] });
    setUpdateUser(true);
    addMessagingTokenToUser(onboardingUser.uid);
    setShowAccountCreated(true);
  };

  const onCancel = () => {
    setShowOTP(false);
    form.setFieldsValue({ phone: '' });
  };

  // Escape hatch when the user is trapped on the phone-verification or
  // onboarding screen (wrong Google account picked, wants to switch, etc).
  // Fully signs out and resets the local wizard state so the main login
  // screen renders again.
  const handleSwitchAccount = () => {
    signOut(auth).finally(() => {
      resetRecaptcha();
      setLoggedMail(null);
      setOnboardingUser(null);
      setShowOTP(false);
      setLocationData(null);
      form.resetFields();
      onboardingForm.resetFields();
    });
  };

  const sendCode = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();
      setLoading(false);
      if (values) onSignup();
    } catch {
      setLoading(false);
    }
  };

  const cardStyle = {
    background: token.colorBgContainer,
    borderRadius: 20,
    padding: '28px 24px',
    boxShadow: `0 8px 32px ${token.colorPrimary}22, 0 2px 8px rgba(0,0,0,0.12)`,
    border: `1px solid ${token.colorBorderSecondary}`,
  };

  const screenStyle = {
    minHeight: '100dvh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 20px',
    background: token.colorBgLayout,
    position: 'relative',
    overflow: 'hidden',
  };

  // ── Onboarding screen (new users only) ────────────────────────────────
  if (onboardingUser) {
    return (
      <div style={screenStyle}>
        <PitchBackground />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          style={{ width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}>

          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <AppLogo size={44} textSize={18} style={{ justifyContent: 'center' }} />
          </div>

          <div style={cardStyle}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <Avatar
                src={onboardingUser.photoURL}
                size={72}
                style={{ marginBottom: 12, border: `3px solid ${token.colorPrimary}33` }}
              />
              <Typography.Title level={5} style={{ margin: 0 }}>
                ¡Bienvenido/a!
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                Completa tu perfil para empezar
              </Typography.Text>
            </div>

            <Form form={onboardingForm} layout="vertical" onFinish={onOnboardingSubmit}>
              <Form.Item
                name="nickname"
                label="Nombre"
                rules={[
                  { required: true, message: 'Ingresa tu nombre.' },
                  { max: 30, message: 'Máximo 30 caracteres.' },
                ]}
                style={{ marginBottom: 12 }}>
                <Input size="large" placeholder="Tu nombre" maxLength={30} showCount />
              </Form.Item>

              <Form.Item
                name="photoURL"
                label="Foto (URL)"
                style={{ marginBottom: 12 }}>
                <Input size="large" placeholder="https://..." />
              </Form.Item>

              <Form.Item
                name="birthDate"
                label="Fecha de nacimiento"
                style={{ marginBottom: 12 }}>
                <DatePicker
                  style={{ width: '100%' }}
                  size="large"
                  // Anchor the calendar to the field (body-anchored panels
                  // drift on mobile when the keyboard resizes the viewport)
                  // and skip the keyboard entirely — it's a date, not text
                  inputReadOnly
                  getPopupContainer={trigger => trigger.parentElement}
                  disabledDate={d => d && d.isAfter(dayjs())}
                  format="DD/MM/YYYY"
                  placeholder="DD/MM/AAAA"
                />
              </Form.Item>

              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item
                    name="position"
                    label="Posición"
                    style={{ marginBottom: 12 }}>
                    <Select size="large">
                      {Object.values(POSITION)
                        .filter(p => p !== POSITION.ANY)
                        .map(p => (
                          <Select.Option key={p} value={p}>{p}</Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="level"
                    label="Nivel"
                    style={{ marginBottom: 12 }}>
                    <Select size="large">
                      {Object.values(LEVELS).map(l => (
                        <Select.Option key={l} value={l}>{l}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="languageSelected"
                label="Idioma"
                style={{ marginBottom: 20 }}>
                <Select size="large">
                  <Select.Option value={LANGUAGES.SPANISH}>Español</Select.Option>
                  <Select.Option value={LANGUAGES.ENGLISH}>English</Select.Option>
                  <Select.Option value={LANGUAGES.ARABIC}>العربية</Select.Option>
                </Select>
              </Form.Item>

              {/* Location */}
              <div style={{ marginBottom: 20 }}>
                <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>
                  {`${formatMessage({ id: 'profile.location.title' })} *`}
                </Typography.Text>
                {locationData ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14 }}>
                      {`📍 ${locationData.city || formatMessage({ id: 'profile.location.saved' })}`}
                    </span>
                    <Button size="small" type="link" onClick={() => setLocationData(null)}>
                      {formatMessage({ id: 'common.change' })}
                    </Button>
                  </div>
                ) : (
                  <>
                    <Button
                      block
                      size="large"
                      loading={loadingGPS}
                      onClick={handleGetGPSLocation}
                      style={{ marginBottom: 8, borderRadius: 10 }}>
                      {`📡 ${formatMessage({ id: 'profile.location.useGps' })}`}
                    </Button>
                    <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', fontSize: 12, marginBottom: 8 }}>
                      {`— ${formatMessage({ id: 'profile.location.or' })} —`}
                    </Typography.Text>
                    <SearchLocationInput
                      setMapsAddress={handleCitySelect}
                      types={['(cities)']}
                      placeholder={formatMessage({ id: 'profile.location.searchCity' })}
                    />
                  </>
                )}
              </div>

              <Button
                block
                type="primary"
                size="large"
                htmlType="submit"
                loading={loading}
                style={{ borderRadius: 12, height: 46 }}>
                Crear cuenta
              </Button>
            </Form>
            <Button
              block
              type="text"
              size="small"
              onClick={handleSwitchAccount}
              style={{ marginTop: 12 }}>
              {formatMessage({ id: 'login.switch.account' })}
            </Button>
          </div>
        </motion.div>
        <div id="recaptcha-container" />
        <ShiftCreatedOverlay
          visible={showAccountCreated}
          icon="🎉"
          title="¡Cuenta Creada!"
          subtitle={`Bienvenido/a, ${onboardingUser?.displayName?.split(' ')[0] || ''}`}
          onDone={() => navigate(getDestination())}
        />
      </div>
    );
  }

  // ── Phone verification screen ──────────────────────────────────────────
  if (loggedMail) {
    return (
      <div style={screenStyle}>
        <PitchBackground />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          style={{ width: '100%', maxWidth: 360, position: 'relative', zIndex: 1 }}>

          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <AppLogo size={52} textSize={22} style={{ justifyContent: 'center' }} />
          </div>

          <div style={cardStyle}>
            <Typography.Title level={5} style={{ marginBottom: 4, textAlign: 'center' }}>
              {showOTP ? formatMessage({ id: 'login.otp.title' }) : formatMessage({ id: 'login.phone.title' })}
            </Typography.Title>
            <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 20, fontSize: 13 }}>
              {showOTP
                ? formatMessage({ id: 'login.otp.description' })
                : formatMessage({ id: 'login.phone.description' }, { name: loggedMail.displayName?.split(' ')[0] })}
            </Typography.Text>

            <Form name="phone" form={form} onFinish={sendCode}>
              {showOTP ? (
                <>
                  <FormItem
                    name="otp"
                    rules={[
                      { required: true, message: formatMessage({ id: 'login.field.code.error' }) },
                      () => ({
                        validator(_, value) {
                          if (!value) return Promise.reject(Error(''));
                          if (Number.isNaN(value)) return Promise.reject(Error(formatMessage({ id: 'login.field.code.validator' })));
                          return Promise.resolve();
                        },
                      }),
                    ]}>
                    <Input.OTP
                      style={{ width: '100%' }}
                      controls={false}
                      onChange={value => form.setFieldsValue({ otp: value })}
                    />
                  </FormItem>

                  <Col style={{ marginTop: 16 }}>
                    <Button
                      block
                      type="primary"
                      size="large"
                      onClick={onOTPVerify}
                      loading={loading}
                      style={{ borderRadius: 12, height: 46 }}>
                      {formatMessage({ id: 'login.otp.verify' })}
                    </Button>
                    <Button block type="link" size="small" onClick={sendCode} loading={loading} style={{ marginTop: 8 }}>
                      {formatMessage({ id: 'login.otp.resend' })}
                    </Button>
                    <Button block danger type="text" size="small" onClick={onCancel} style={{ marginTop: 4 }}>
                      {formatMessage({ id: 'common.cancel' })}
                    </Button>
                  </Col>
                </>
              ) : (
                <>
                  <FormItem
                    name="phone"
                    rules={[
                      { required: true, message: formatMessage({ id: 'login.field.phone.error' }) },
                      () => ({
                        validator(_, value) {
                          if (!value) return Promise.reject(Error(''));
                          if (!/^\d{6,12}$/.test(value)) return Promise.reject(Error(formatMessage({ id: 'login.field.phone.validator' })));
                          return Promise.resolve();
                        },
                      }),
                    ]}>
                    <Input
                      size="large"
                      inputMode="numeric"
                      placeholder="611 111 111"
                      addonBefore={
                        <Form.Item name="countryCode" noStyle initialValue="34">
                          <Select
                            showSearch
                            style={{ width: 90 }}
                            popupMatchSelectWidth={280}
                            optionFilterProp="title"
                            filterOption={(input, option) => {
                              const q = (input ?? '').toLowerCase().replace(/^\+/, '');
                              return (
                                (option?.title ?? '').toLowerCase().includes(q) ||
                                String(option?.value ?? '').includes(q)
                              );
                            }}
                            options={COUNTRY_CODES.map(c => ({
                              value: c.code,
                              label: `${c.flag} +${c.code}`,
                              title: `${c.label} +${c.code}`,
                            }))}
                          />
                        </Form.Item>
                      }
                    />
                  </FormItem>

                  <Button
                    block
                    type="primary"
                    size="large"
                    style={{ marginTop: 8, borderRadius: 12, height: 46 }}
                    onClick={sendCode}
                    loading={loading}>
                    {formatMessage({ id: 'login.otp.send' })}
                  </Button>
                </>
              )}
            </Form>
            <Button
              block
              type="text"
              size="small"
              onClick={handleSwitchAccount}
              style={{ marginTop: 12 }}>
              {formatMessage({ id: 'login.switch.account' })}
            </Button>
          </div>
        </motion.div>
        <div id="recaptcha-container" />
      </div>
    );
  }

  // ── Main login screen ──────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 20px',
      background: token.colorBgLayout,
      position: 'relative',
      overflow: 'hidden',
    }}>
      <PitchBackground />

      {/* Ambient glow blobs */}
      <div style={{
        position: 'absolute',
        top: '-10%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '80vw',
        height: '50vw',
        maxWidth: 500,
        borderRadius: '50%',
        background: `radial-gradient(ellipse, ${token.colorPrimary}22 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      <div style={{ width: '100%', maxWidth: 380, position: 'relative', zIndex: 1 }}>

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          style={{ textAlign: 'center', marginBottom: 40 }}>

          <AppLogo size={56} textSize={26} style={{ justifyContent: 'center', marginBottom: 16, maxWidth: '100%' }} />

          <Typography.Title
            level={4}
            style={{
              margin: 0,
              fontWeight: 300,
              fontSize: 15,
              color: token.colorTextSecondary,
              letterSpacing: '0.2px',
            }}>
            Organiza tus partidos de fútbol
          </Typography.Title>

          {/* Feature pills */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
            {['Partidos', 'Ligas', 'Stats', 'Equipos'].map(label => (
              <span
                key={label}
                style={{
                  fontSize: 11,
                  padding: '3px 10px',
                  borderRadius: 100,
                  background: `${token.colorPrimary}18`,
                  color: token.colorPrimary,
                  fontWeight: 500,
                  border: `1px solid ${token.colorPrimary}30`,
                }}>
                {label}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Sign-in card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}>
          <div style={{
            background: token.colorBgContainer,
            borderRadius: 20,
            padding: '28px 24px',
            boxShadow: `0 8px 32px ${token.colorPrimary}22, 0 2px 8px rgba(0,0,0,0.1)`,
            border: `1px solid ${token.colorBorderSecondary}`,
          }}>
            {inApp ? (
              <>
                <Typography.Text strong style={{ display: 'block', textAlign: 'center', marginBottom: 8, fontSize: 14 }}>
                  {formatMessage({ id: 'login.inapp.title' })}
                </Typography.Text>
                {isIos() && (
                  <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 20, fontSize: 13, lineHeight: 1.5 }}>
                    {formatMessage({ id: 'login.inapp.ios' })}
                  </Typography.Text>
                )}
                <Button
                  size="large"
                  block
                  type="primary"
                  onClick={handleOpenExternal}
                  style={{ height: 48, borderRadius: 12, fontWeight: 500, marginTop: isIos() ? 0 : 12 }}>
                  {formatMessage({ id: isIos() ? 'login.inapp.copy' : 'login.inapp.open' })}
                </Button>
              </>
            ) : (
              <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 20, fontSize: 13 }}>
                Inicia sesión para continuar
              </Typography.Text>
            )}

            {!inApp && (
            <Button
              size="large"
              block
              onClick={showPopup}
              loading={loading}
              style={{
                height: 48,
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                fontWeight: 500,
                fontSize: 15,
                border: `1px solid ${token.colorBorderSecondary}`,
              }}
              icon={
                <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                  <path fill="none" d="M0 0h48v48H0z" />
                </svg>
              }>
              Ingresar con Google
            </Button>
            )}
          </div>
        </motion.div>

        {/* How it works — quick pitch for visitors landing from ads */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          style={{ marginTop: 28 }}>
          <div style={{
            display: 'flex',
            gap: 8,
            padding: '18px 14px',
            borderRadius: 16,
            background: token.colorBgContainer,
            border: `1px solid ${token.colorBorderSecondary}`,
          }}>
            {HOW_STEPS.map(step => (
              <div key={step.titleId} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: 22, marginBottom: 6 }}>{step.emoji}</div>
                <Typography.Text strong style={{ display: 'block', fontSize: 12, marginBottom: 2 }}>
                  {formatMessage({ id: step.titleId })}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 11, lineHeight: 1.4 }}>
                  {formatMessage({ id: step.descId })}
                </Typography.Text>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Feature highlights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          style={{ marginTop: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {FEATURES.map(f => (
              <div
                key={f.titleId}
                style={{
                  padding: '14px',
                  borderRadius: 14,
                  background: token.colorBgContainer,
                  border: `1px solid ${token.colorBorderSecondary}`,
                  position: 'relative',
                  overflow: 'hidden',
                }}>
                <div style={{
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: f.accent,
                  opacity: 0.08,
                  pointerEvents: 'none',
                }} />
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: 10,
                  background: `${f.accent}18`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  color: f.accent,
                  marginBottom: 8,
                }}>
                  {f.icon}
                </div>
                <Typography.Text strong style={{ display: 'block', fontSize: 13, marginBottom: 2 }}>
                  {formatMessage({ id: f.titleId })}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 11, lineHeight: 1.45 }}>
                  {formatMessage({ id: f.descId })}
                </Typography.Text>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Footer note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          style={{ textAlign: 'center', marginTop: 24 }}>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            Al continuar aceptas los términos de uso
          </Typography.Text>
        </motion.div>
      </div>

      <div id="recaptcha-container" />
    </div>
  );
};

export default Login;
